import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
// Removed react-dnd - using native mouse events for better performance
import {
	faArrowsAlt,
	faExpand,
	faGripVertical,
} from '@fortawesome/pro-solid-svg-icons';
import type { FilterState } from '@supply-mover-2-0/core';
import { Platform } from '@supply-mover-2-0/core';
// Sibling-package imports — relative so this file (now in libs/web-ui)
// doesn't self-import via `@supply-mover-2-0/web-ui`.
import { Icon } from '../components/base/Icon';
import { Text } from '../components/base/Text';
import { useThemeColors } from '../providers/ThemeProvider';
import { useTranslation } from '../providers/TranslationProvider';
import { cn } from '../utils/cn';
import type {
	WidgetDataAdapter,
	WidgetFilterAdapter,
} from './types/adapters.types';
import type { WidgetConfig, WidgetData } from './types/widget.types';
import { WidgetContainer } from './WidgetContainer';

interface WidgetGridProps {
	widgets: WidgetConfig[];
	editMode: boolean;
	onLayoutChange: (widgets: WidgetConfig[]) => void;
	onRemoveWidget: (widgetId: string) => void;
	onWidgetTitleChange?: (widgetId: string, newTitle: string) => void;
	/**
	 * Optional handler invoked when a widget's gear icon is clicked.
	 * When set, the gear button shows in BOTH view and edit modes.
	 * When omitted, no gear button renders at all (legacy CRM
	 * behaviour). Storefront designer wires this to open the per-
	 * widget settings drawer (mode/rankBy/limit/etc).
	 */
	onWidgetSettings?: (widgetId: string) => void;
	/** Dashboard-level filters to pass to widgets */
	dashboardFilters?: FilterState;
	/** Callback when widget filters change */
	onWidgetFiltersChange?: (widgetId: string, filters: FilterState) => void;
	/** Whether dashboard is fully initialized */
	dashboardInitialized?: boolean;

	/**
	 * Data + filter adapter slots. The grid is presentational and
	 * doesn't know what's behind the data — CRM injects an adapter
	 * backed by `DashboardService` + `widgetDataStore` (report-driven
	 * widgets); the storefront injects an adapter backed by its own
	 * promo widget data store. Required so the grid can live in
	 * libs/web-ui without depending on either consumer's services.
	 */
	dataAdapter: WidgetDataAdapter;
	filterAdapter: WidgetFilterAdapter;

	/**
	 * Widget renderer slot. The grid is consumer-agnostic; the
	 * consumer controls how each widget body renders. CRM passes a
	 * renderer that delegates to `ReportWidgetLoader`; storefront
	 * passes one that maps widget.type → promo widget components.
	 * Required so the grid stays free of report-loader / promo-loader
	 * coupling.
	 */
	renderWidget: (
		widget: WidgetConfig,
		data: WidgetData | undefined,
		ctx: {
			dashboardFilters?: FilterState;
			onFiltersChange?: (filters: FilterState) => void;
			refreshKey?: number;
			refreshTrigger?: number;
		},
	) => React.ReactNode;

	/**
	 * Optional slots forwarded to `<WidgetContainer>` so the consumer
	 * can wire its CRM-specific filter manager + fullscreen renderer
	 * (or skip both for the storefront, which has no per-widget
	 * filters and can reuse the same renderer for fullscreen).
	 */
	renderFilterManager?: React.ComponentProps<
		typeof WidgetContainer
	>['renderFilterManager'];
	renderFullscreenWidget?: React.ComponentProps<
		typeof WidgetContainer
	>['renderFullscreenWidget'];
	/**
	 * Strip widget chrome (title bar + icons + drag handle + card
	 * frame) so each widget renders its body directly. Storefront
	 * uses this — end users can't interact with that chrome and it
	 * eats into the widget's saved height. Defaults to false so
	 * CRM dashboards keep their full chrome.
	 */
	hideWidgetChrome?: boolean;

	/**
	 * Optional handler invoked when the user drops a widget tile from
	 * an external source (e.g. the WidgetSelectorPanel sidebar) onto
	 * the grid. The grid resolves the target grid cell using the same
	 * pixels-to-grid math the internal drag uses and runs the smart
	 * reflow against existing widgets before calling — by the time the
	 * parent receives the call:
	 *  - `position` is where the new widget should land (no auto-find).
	 *  - `reflowedWidgets` contains the *existing* widgets with their
	 *    post-reflow positions (a copy of `widgets` with positions
	 *    replaced for any that had to move). The parent should use this
	 *    as the base when committing the new layout, otherwise its own
	 *    closure-captured `widgets` snapshot will overwrite the reflow.
	 *
	 * The widget type is carried in the
	 * `application/x-supplymover-widget-type` dataTransfer slot; the
	 * dimensions (`<w>x<h>`, falls back to 2x2) come from
	 * `application/x-supplymover-widget-dims`. When the prop is omitted,
	 * external drags are ignored entirely — the grid still handles
	 * internal widget-on-widget reordering as before.
	 */
	onExternalWidgetDrop?: (
		widgetType: string,
		position: { col: number; row: number; width: number; height: number },
		reflowedWidgets: WidgetConfig[],
	) => void;
}

const EXTERNAL_WIDGET_MIME = 'application/x-supplymover-widget-type';
const EXTERNAL_WIDGET_DIMS_MIME = 'application/x-supplymover-widget-dims';
// Synthetic id used while previewing where an external widget will land.
// `handleSmartDragReposition` keys on this to know which entry in the
// layouts dict represents "the thing being dropped". It never escapes the
// grid component — the parent gets a real id assigned by createWidget.
const EXTERNAL_DRAG_PREVIEW_ID = '__external-drag-preview__';

// Grid system constants matching original design
const GRID_PADDING = 0;
const GRID_GAP = 16;
const GRID_COLUMNS = 4; // Original 4-column system
const MIN_WIDGET_HEIGHT = 200;
const GRID_ROW_HEIGHT = 100; // Base height unit for grid rows
const WEB_MOBILE_BREAKPOINT = 768; // Breakpoint for responsive mobile layout on web

// Edge auto-scroll while dragging a widget. Without this you can only drop a
// widget within the visible viewport — a real problem on a phone where the grid
// is taller than the screen. When the pointer nears the top/bottom of the
// scroll container we scroll it, faster the closer to the edge.
const AUTOSCROLL_EDGE_PX = 70; // distance from an edge that triggers scrolling
const AUTOSCROLL_MAX_SPEED = 16; // px/frame at the very edge

/** Nearest vertically-scrollable ancestor of `el`, or the window if none. */
function findScrollParent(el: HTMLElement | null): HTMLElement | Window {
	let node: HTMLElement | null = el?.parentElement ?? null;
	while (node != null) {
		const style = window.getComputedStyle(node);
		if (
			(style.overflowY === 'auto' || style.overflowY === 'scroll') &&
			node.scrollHeight > node.clientHeight
		) {
			return node;
		}
		node = node.parentElement;
	}
	return window;
}

function getScrollTop(sc: HTMLElement | Window): number {
	// window.scrollY / element.scrollTop are always numbers (0 at the top).
	return sc instanceof Window ? window.scrollY : sc.scrollTop;
}

/** Viewport-relative top/bottom edges of the scroll container. */
function getScrollViewport(sc: HTMLElement | Window): {
	top: number;
	bottom: number;
} {
	if (sc instanceof Window) {
		return { top: 0, bottom: window.innerHeight };
	}
	const rect = sc.getBoundingClientRect();
	return { top: rect.top, bottom: rect.bottom };
}

function scrollByY(sc: HTMLElement | Window, dy: number): void {
	if (sc instanceof Window) {
		window.scrollBy(0, dy);
	} else {
		sc.scrollTop += dy;
	}
}

interface GridPosition {
	col: number;
	row: number;
	width: number; // in columns
	height: number; // in rows
}

// Support for both old and new position formats
interface WidgetPosition {
	// New format
	col?: number;
	row?: number;
	width?: number;
	height?: number;
	// Old format (for backward compatibility)
	x?: number;
	y?: number;
	w?: number;
	h?: number;
}

/**
 * WidgetConfig.size is declared as the WidgetSize string union, but persisted
 * layouts round-trip it as a `{ width, height }` grid-span object (see
 * updateWidgetConfigs / the external-drop merge). Read the numeric spans only
 * when the object shape is actually present.
 */
const getSizeSpan = (
	size: WidgetConfig['size'] | { width: number; height: number } | undefined,
	axis: 'width' | 'height',
): number | undefined =>
	typeof size === 'object' && size != null ? size[axis] : undefined;

interface WidgetLayout {
	id: string;
	position: GridPosition;
	isDragging?: boolean;
	isResizing?: boolean;
}

// Native drag implementation - no longer need ItemTypes

interface DraggedWidget {
	id: string;
	position: GridPosition;
}

// Individual draggable widget component
interface ResizeInfo {
	width: number;
	height: number;
}

// Deep equality check for filter objects to prevent unnecessary re-renders
const deepEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) {
		return true;
	}
	if (obj1 == null || obj2 == null) {
		return false;
	}
	if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
		return obj1 === obj2;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		if (!keys2.includes(key)) {
			return false;
		}
		if (!deepEqual(obj1[key], obj2[key])) {
			return false;
		}
	}

	return true;
};

const DraggableWidgetBase: React.FC<{
	widget: WidgetConfig;
	layout: WidgetLayout;
	editMode: boolean;
	onRemove: (widgetId: string) => void;
	onSettings?: (widgetId: string) => void;
	onTitleChange?: (widgetId: string, newTitle: string) => void;
	dashboardFilters?: FilterState;
	onFiltersChange?: (widgetId: string, filters: FilterState) => void;
	onDrop: (draggedId: string, targetId: string) => void;
	columnWidth: number;
	GRID_GAP: number;
	GRID_ROW_HEIGHT: number;
	isAffected: boolean;
	previewGridPosition: GridPosition | null;
	onDragStart: () => void;
	onDragEnd: (widgetId: string, x: number, y: number) => void;
	onDragMove: (widgetId: string, previewPosition: GridPosition) => void;
	setPreviewGridPosition: (position: GridPosition | null) => void;
	pixelsToGridPositionDragOnly: (
		x: number,
		y: number,
		originalWidth: number,
		originalHeight: number,
	) => GridPosition;
	isResizing: boolean;
	onResizeStart: () => void;
	onResizeMove: (
		widgetId: string,
		previewPosition: GridPosition,
		oldPosition: GridPosition,
	) => void;
	onResizeEnd: (widgetId: string, width: number, height: number) => void;
	gridPositionToPixels: (position: GridPosition) => {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	pixelsToGridPosition: (
		x: number,
		y: number,
		width: number,
		height: number,
	) => GridPosition;
	snapToGridWidth: (pixelWidth: number) => number;
	snapToGridHeight: (pixelHeight: number) => number;
	widgetData: WidgetData | undefined;
	onRefresh: (widgetId: string) => void;
	filterAdapter: WidgetFilterAdapter;
	renderWidget: WidgetGridProps['renderWidget'];
	renderFilterManager?: WidgetGridProps['renderFilterManager'];
	renderFullscreenWidget?: WidgetGridProps['renderFullscreenWidget'];
	/**
	 * Forwarded to `<WidgetContainer hideChrome={...}>` — strips the
	 * widget header/icons/drag handle. Storefront sets this true so
	 * the read-only render shows only the promo body.
	 */
	hideChrome?: boolean;
}> = ({
	widget,
	layout,
	editMode,
	onRemove,
	onSettings,
	onTitleChange,
	dashboardFilters,
	onFiltersChange,
	onDrop,
	columnWidth,
	GRID_GAP,
	GRID_ROW_HEIGHT,
	isAffected,
	previewGridPosition,
	onDragStart,
	onDragEnd,
	onDragMove,
	setPreviewGridPosition,
	pixelsToGridPositionDragOnly,
	isResizing,
	onResizeStart,
	onResizeMove,
	onResizeEnd,
	gridPositionToPixels,
	pixelsToGridPosition,
	snapToGridWidth,
	snapToGridHeight,
	widgetData,
	onRefresh,
	filterAdapter,
	renderWidget,
	renderFilterManager,
	renderFullscreenWidget,
	hideChrome = false,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [dragStartPosition, setDragStartPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);

	// Native drag state (similar to resize)
	const [isDraggingLocal, setIsDraggingLocal] = useState(false);
	const [dragStart, setDragStart] = useState({
		x: 0,
		y: 0,
		widgetX: 0,
		widgetY: 0,
	});
	const [currentDragPosition, setCurrentDragPosition] = useState<{
		left: number;
		top: number;
	} | null>(null);
	const [dragMouseOffset, setDragMouseOffset] = useState<{
		x: number;
		y: number;
	} | null>(null);

	// Resize state
	const [isResizingLocal, setIsResizingLocal] = useState(false);
	const [resizeStart, setResizeStart] = useState({
		x: 0,
		y: 0,
		width: 0,
		height: 0,
	});
	const [isHoveringResize, setIsHoveringResize] = useState(false);
	const [lastPreviewPosition, setLastPreviewPosition] =
		useState<GridPosition | null>(null);
	const [currentResize, setCurrentResize] = useState<ResizeInfo | null>(null);

	// Edge auto-scroll state during a drag. Refs (not state) so the rAF loop
	// always reads live values without stale closures. Captured at pointer-down:
	// the scroll container + its initial scrollTop (so the position math can add
	// the scroll delta as the page scrolls under the dragged widget).
	const scrollParentRef = useRef<HTMLElement | Window | null>(null);
	const dragStartScrollTopRef = useRef(0);
	const lastPointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const autoScrollRafRef = useRef<number | null>(null);
	const autoScrollSpeedRef = useRef(0);
	// Live ref to the latest preview-apply fn so the rAF loop never goes stale.
	const applyDragPreviewRef = useRef<
		(clientX: number, clientY: number) => void
	>(() => undefined);

	// Simple callbacks that bind widget.id
	// These will change when props change, but WidgetContainer's memo handles it correctly
	const handleRefresh = useCallback(() => {
		onRefresh(widget.id);
	}, [onRefresh, widget.id]);

	const handleRemove = useCallback(() => {
		onRemove(widget.id);
	}, [onRemove, widget.id]);

	// Bound onSettings — undefined when no parent handler so the
	// container hides the gear icon.
	const handleSettings = useCallback(() => {
		onSettings?.(widget.id);
	}, [onSettings, widget.id]);

	const handleTitleChange = useCallback(
		(newTitle: string) => {
			if (onTitleChange) {
				onTitleChange(widget.id, newTitle);
			}
		},
		[onTitleChange, widget.id],
	);

	const handleFiltersChange = useCallback(
		(filters: FilterState) => {
			if (onFiltersChange) {
				onFiltersChange(widget.id, filters);
			}
		},
		[onFiltersChange, widget.id],
	);

	const handleWidgetFiltersChange = useCallback(
		(widgetId: string, overrides: FilterState, filterConfig: any) => {
			if (onFiltersChange) {
				// Pass both overrides and the filter config to the parent
				// Note: onFiltersChange expects (widgetId, filters) but the third param is being ignored
				// The filters ARE the overrides in this context
				onFiltersChange(widget.id, overrides);
			}
		},
		[onFiltersChange, widget.id],
	);

	// Calculate pixel position from grid position
	const pixelPosition = {
		left: layout.position.col * (columnWidth + GRID_GAP),
		top: layout.position.row * (GRID_ROW_HEIGHT + GRID_GAP),
		width:
			layout.position.width * columnWidth +
			(layout.position.width - 1) * GRID_GAP,
		height:
			layout.position.height * GRID_ROW_HEIGHT +
			(layout.position.height - 1) * GRID_GAP,
	};

	// Native drag handlers (similar to resize implementation).
	// Uses Pointer Events (not Mouse Events) so touch input works on
	// mobile/tablet — pointer events fire for mouse, pen, and touch, so
	// the same handler unifies desktop and touch without a parallel set.
	const handleDragPointerDown = (event: React.PointerEvent) => {
		event.preventDefault();
		event.stopPropagation();

		// Capture the pointer so move/up keep firing on this element even if
		// the finger/cursor slides outside it during the drag.
		(event.target as HTMLElement).setPointerCapture(event.pointerId);

		// Capture the pointer offset within the widget
		const widgetRect = ref.current?.getBoundingClientRect();
		if (widgetRect) {
			setDragMouseOffset({
				x: event.clientX - widgetRect.left,
				y: event.clientY - widgetRect.top,
			});
		}

		// Capture the scroll container + its current scrollTop so the drag can
		// auto-scroll near the edges and stay accurate as the page scrolls.
		scrollParentRef.current = findScrollParent(ref.current);
		dragStartScrollTopRef.current = getScrollTop(scrollParentRef.current);
		lastPointerRef.current = { x: event.clientX, y: event.clientY };

		setIsDraggingLocal(true);
		setDragStart({
			x: event.clientX,
			y: event.clientY,
			widgetX: pixelPosition.left,
			widgetY: pixelPosition.top,
		});
		onDragStart();
	};

	// Compute + apply the drag preview from a pointer position, adding the
	// scroll delta so the widget keeps tracking the finger as the page scrolls
	// under it (edge auto-scroll below).
	const applyDragPreview = useCallback(
		(clientX: number, clientY: number) => {
			const scrollDelta =
				scrollParentRef.current != null
					? getScrollTop(scrollParentRef.current) -
						dragStartScrollTopRef.current
					: 0;

			const newLeft = dragStart.widgetX + (clientX - dragStart.x);
			const newTop = dragStart.widgetY + (clientY - dragStart.y) + scrollDelta;

			// Update visual position for smooth dragging
			setCurrentDragPosition({ left: newLeft, top: newTop });

			// Calculate grid position for preview
			const previewPosition = pixelsToGridPositionDragOnly(
				newLeft,
				newTop,
				layout.position.width,
				layout.position.height,
			);

			// Only update preview if grid position changed
			if (
				!lastPreviewPosition ||
				lastPreviewPosition.col !== previewPosition.col ||
				lastPreviewPosition.row !== previewPosition.row
			) {
				setLastPreviewPosition(previewPosition);
				setPreviewGridPosition(previewPosition);
				onDragMove(widget.id, previewPosition);
			}
		},
		[
			dragStart,
			pixelsToGridPositionDragOnly,
			layout.position,
			lastPreviewPosition,
			setPreviewGridPosition,
			onDragMove,
			widget.id,
		],
	);
	// Keep a live ref so the rAF auto-scroll loop always calls the latest fn.
	applyDragPreviewRef.current = applyDragPreview;

	const stopAutoScroll = useCallback(() => {
		if (autoScrollRafRef.current != null) {
			cancelAnimationFrame(autoScrollRafRef.current);
			autoScrollRafRef.current = null;
		}
		autoScrollSpeedRef.current = 0;
	}, []);

	// rAF loop: scroll the container a step, then re-apply the preview from the
	// last pointer position so the dragged widget keeps following the finger.
	const runAutoScroll = useCallback(() => {
		const sc = scrollParentRef.current;
		if (sc == null || autoScrollSpeedRef.current === 0) {
			autoScrollRafRef.current = null;
			return;
		}
		scrollByY(sc, autoScrollSpeedRef.current);
		applyDragPreviewRef.current(
			lastPointerRef.current.x,
			lastPointerRef.current.y,
		);
		autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
	}, []);

	// Set the auto-scroll speed from the pointer's distance to the container's
	// top/bottom edge, and start/stop the rAF loop accordingly.
	const updateAutoScroll = useCallback(
		(clientY: number) => {
			const sc = scrollParentRef.current;
			if (sc == null) {
				return;
			}
			const { top, bottom } = getScrollViewport(sc);
			let speed = 0;
			if (clientY > bottom - AUTOSCROLL_EDGE_PX) {
				const intensity = Math.min(
					1,
					(clientY - (bottom - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX,
				);
				speed = intensity * AUTOSCROLL_MAX_SPEED;
			} else if (clientY < top + AUTOSCROLL_EDGE_PX) {
				const intensity = Math.min(
					1,
					(top + AUTOSCROLL_EDGE_PX - clientY) / AUTOSCROLL_EDGE_PX,
				);
				speed = -intensity * AUTOSCROLL_MAX_SPEED;
			}
			autoScrollSpeedRef.current = speed;
			if (speed !== 0 && autoScrollRafRef.current == null) {
				autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
			} else if (speed === 0 && autoScrollRafRef.current != null) {
				stopAutoScroll();
			}
		},
		[runAutoScroll, stopAutoScroll],
	);

	const handleDragMouseMove = useCallback(
		(event: PointerEvent) => {
			if (!isDraggingLocal) {
				return;
			}
			lastPointerRef.current = { x: event.clientX, y: event.clientY };
			applyDragPreview(event.clientX, event.clientY);
			updateAutoScroll(event.clientY);
		},
		[isDraggingLocal, applyDragPreview, updateAutoScroll],
	);

	const handleDragMouseUp = useCallback(
		(event: PointerEvent) => {
			if (!isDraggingLocal) {
				return;
			}

			stopAutoScroll();
			setIsDraggingLocal(false);

			const scrollDelta =
				scrollParentRef.current != null
					? getScrollTop(scrollParentRef.current) -
						dragStartScrollTopRef.current
					: 0;
			const finalLeft = dragStart.widgetX + (event.clientX - dragStart.x);
			const finalTop =
				dragStart.widgetY + (event.clientY - dragStart.y) + scrollDelta;

			// Clear all drag states
			setCurrentDragPosition(null);
			setPreviewGridPosition(null);
			setLastPreviewPosition(null);
			setDragMouseOffset(null);
			scrollParentRef.current = null;

			// Apply the final position
			onDragEnd(widget.id, finalLeft, finalTop);
		},
		[
			isDraggingLocal,
			dragStart,
			setPreviewGridPosition,
			onDragEnd,
			widget.id,
			stopAutoScroll,
		],
	);

	// Add global pointer listeners for drag (similar to resize). Pointer
	// events cover mouse + touch + pen, so this preserves desktop drag
	// while enabling touch-drag on mobile.
	useEffect(() => {
		if (isDraggingLocal) {
			const handlePointerMove = (e: PointerEvent) => {
				handleDragMouseMove(e);
			};
			const handlePointerUp = (e: PointerEvent) => {
				handleDragMouseUp(e);
			};

			document.addEventListener('pointermove', handlePointerMove);
			document.addEventListener('pointerup', handlePointerUp);

			return () => {
				document.removeEventListener('pointermove', handlePointerMove);
				document.removeEventListener('pointerup', handlePointerUp);
				// Cancel any in-flight edge auto-scroll if the drag ends/unmounts.
				stopAutoScroll();
			};
		}
		return undefined;
	}, [isDraggingLocal, handleDragMouseMove, handleDragMouseUp, stopAutoScroll]);

	// Resize handlers
	const handleResizePointerDown = (event: React.PointerEvent) => {
		event.preventDefault();
		event.stopPropagation();

		// Capture the pointer so move/up keep firing during a touch-resize.
		(event.target as HTMLElement).setPointerCapture(event.pointerId);

		setIsResizingLocal(true);
		setResizeStart({
			x: event.clientX,
			y: event.clientY,
			width: pixelPosition.width,
			height: pixelPosition.height,
		});
		setLastPreviewPosition(null);
		onResizeStart();
	};

	const handleResizeMouseMove = useCallback(
		(event: PointerEvent) => {
			if (!isResizingLocal) {
				return;
			}

			const deltaX = event.clientX - resizeStart.x;
			const deltaY = event.clientY - resizeStart.y;

			// Phase 1: Smooth mouse following - no snapping during drag
			const rawWidth = Math.max(columnWidth, resizeStart.width + deltaX);
			const rawHeight = Math.max(GRID_ROW_HEIGHT, resizeStart.height + deltaY);

			// Update visual size for smooth resize
			setCurrentResize({ width: rawWidth, height: rawHeight });

			// Calculate what the snapped dimensions would be for preview
			const previewWidth = snapToGridWidth(rawWidth);
			const previewHeight = snapToGridHeight(rawHeight);

			// Calculate the preview grid position - keep current position but with new size
			const previewPosition = {
				col: layout.position.col,
				row: layout.position.row,
				width: Math.max(
					1,
					Math.min(
						GRID_COLUMNS - layout.position.col,
						Math.round(previewWidth / (columnWidth + GRID_GAP)),
					),
				),
				height: Math.max(
					1,
					Math.round(previewHeight / (GRID_ROW_HEIGHT + GRID_GAP)),
				),
			};

			// Set preview grid position for visual feedback
			setPreviewGridPosition(previewPosition);

			// Track preview position changes for final repositioning
			const positionChanged =
				!lastPreviewPosition ||
				lastPreviewPosition.width !== previewPosition.width ||
				lastPreviewPosition.height !== previewPosition.height;

			if (positionChanged) {
				setLastPreviewPosition(previewPosition);
				onResizeMove(widget.id, previewPosition, layout.position);
			}
		},
		[
			isResizingLocal,
			resizeStart,
			columnWidth,
			GRID_ROW_HEIGHT,
			snapToGridWidth,
			snapToGridHeight,
			layout.position,
			GRID_GAP,
			setPreviewGridPosition,
			lastPreviewPosition,
			setLastPreviewPosition,
			onResizeMove,
			widget.id,
		],
	);

	const handleResizeMouseUp = useCallback(
		(event: PointerEvent) => {
			if (!isResizingLocal) {
				return;
			}

			setIsResizingLocal(false);

			const deltaX = event.clientX - resizeStart.x;
			const deltaY = event.clientY - resizeStart.y;

			// Phase 2: Apply grid snapping to final dimensions
			const rawWidth = Math.max(columnWidth, resizeStart.width + deltaX);
			const rawHeight = Math.max(GRID_ROW_HEIGHT, resizeStart.height + deltaY);

			const finalWidth = snapToGridWidth(rawWidth);
			const finalHeight = snapToGridHeight(rawHeight);

			// Clear all preview states
			setPreviewGridPosition(null);
			setLastPreviewPosition(null);
			setCurrentResize(null);

			// Apply the snapped dimensions to the grid system
			onResizeEnd(widget.id, finalWidth, finalHeight);
		},
		[
			isResizingLocal,
			resizeStart,
			columnWidth,
			GRID_ROW_HEIGHT,
			snapToGridWidth,
			snapToGridHeight,
			setPreviewGridPosition,
			setLastPreviewPosition,
			setCurrentResize,
			onResizeEnd,
			widget.id,
		],
	);

	// Add global pointer listeners for resize. Pointer events cover
	// mouse + touch + pen, so this preserves desktop resize while
	// enabling touch-resize on mobile.
	useEffect(() => {
		if (isResizingLocal) {
			const handlePointerMove = (e: PointerEvent) => {
				handleResizeMouseMove(e);
			};
			const handlePointerUp = (e: PointerEvent) => {
				handleResizeMouseUp(e);
			};

			document.addEventListener('pointermove', handlePointerMove);
			document.addEventListener('pointerup', handlePointerUp);

			return () => {
				document.removeEventListener('pointermove', handlePointerMove);
				document.removeEventListener('pointerup', handlePointerUp);
			};
		}
		return undefined;
	}, [isResizingLocal, handleResizeMouseMove, handleResizeMouseUp]);

	// Calculate drag position - use currentDragPosition for smooth following, or pixel position
	const dragPosition = currentDragPosition
		? {
				left: currentDragPosition.left,
				top: currentDragPosition.top,
				width: pixelPosition.width,
				height: pixelPosition.height,
			}
		: pixelPosition;

	return (
		<>
			{/* Ghost widget at original position when dragging */}
			{isDraggingLocal && (
				<div
					className="absolute pointer-events-none"
					style={{
						left: `${pixelPosition.left}px`,
						top: `${pixelPosition.top}px`,
						width: `${pixelPosition.width}px`,
						height: `${pixelPosition.height}px`,
						opacity: 0.3,
						zIndex: 1,
					}}
				>
					{/* Simple placeholder instead of rendering actual content */}
					<div
						className={cn(
							'w-full h-full rounded-xl shadow-sm',
							colors.classes.bgPrimary,
							'border-2 border-dashed',
							colors.classes.borderDefault,
						)}
					>
						<div
							className={cn(
								'px-4 py-3 rounded-t-xl',
								colors.classes.bgSecondary,
							)}
						>
							<Text
								className={cn(
									'text-base font-bold',
									colors.classes.textPrimary,
								)}
							>
								{t(widget.title)}
							</Text>
						</div>
						<div className="p-4 flex items-center justify-center">
							<Text className={cn('text-sm', colors.classes.textMuted)}>
								{t('Original Position')}
							</Text>
						</div>
					</div>
				</div>
			)}

			{/* Main widget */}
			<div
				ref={ref}
				className={cn(
					'absolute',
					!isDraggingLocal && !isResizingLocal && 'transition-all duration-300',
				)}
				style={{
					left: `${dragPosition.left}px`,
					top: `${dragPosition.top}px`,
					width: `${currentResize?.width || dragPosition.width}px`,
					height: `${currentResize?.height || dragPosition.height}px`,
					zIndex: isDraggingLocal ? 50 : 'auto',
				}}
			>
				{/* Drag handle for edit mode */}
				{editMode && (
					<div className="absolute -top-3 left-0 right-0 flex justify-center z-20 pointer-events-none">
						<div
							className={cn(
								'px-2 py-1 rounded-t-md text-xs flex items-center gap-1 cursor-move pointer-events-auto touch-none',
								colors.classes.primaryButton,
							)}
							onPointerDown={handleDragPointerDown}
						>
							<Icon icon={faGripVertical} size={12} />
							{(() => {
								const text = t('Drag to move');
								if (typeof text !== 'string') {
									// Error: Non-string returned from translation:
									return String(text);
								}
								return text;
							})()}
						</div>
					</div>
				)}

				{/* Resize handles */}
				{editMode && (
					<>
						{/* Bottom-right resize handle */}
						<div
							onPointerDown={handleResizePointerDown}
							onMouseEnter={() => {
								setIsHoveringResize(true);
							}}
							onMouseLeave={() => {
								setIsHoveringResize(false);
							}}
							className="absolute -bottom-1 -right-1 w-8 h-8 cursor-nwse-resize z-20 flex items-center justify-center touch-none"
							style={{
								backgroundColor: isHoveringResize
									? '#10b981'
									: 'rgba(16, 185, 129, 0.9)',
								borderRadius: '50%',
								border: '2px solid #ffffff',
								boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
							}}
						>
							<Icon
								icon={faExpand}
								size={14}
								className={colors.classes.textOnPrimary}
							/>
						</div>
					</>
				)}

				<WidgetContainer
					config={widget}
					data={widgetData}
					onRefresh={handleRefresh}
					onRemove={handleRemove}
					onSettings={onSettings != null ? handleSettings : undefined}
					onTitleChange={handleTitleChange}
					editMode={editMode}
					dashboardFilters={dashboardFilters}
					availableFilters={filterAdapter.getReportFilters(
						widget.reportPath || widget.type,
					)}
					customFilters={filterAdapter.getCustomFiltersForWidget(
						widget.reportPath || widget.type,
					)}
					dateRangeOptions={filterAdapter.getReportDateRangeOptions(
						widget.reportPath || widget.type,
					)}
					dateRangeCalculator={filterAdapter.getReportDateRangeCalculator(
						widget.reportPath || widget.type,
					)}
					onWidgetFiltersChange={handleWidgetFiltersChange}
					renderFilterManager={renderFilterManager}
					renderFullscreenWidget={renderFullscreenWidget}
					hideChrome={hideChrome}
				>
					{renderWidget(widget, widgetData, {
						dashboardFilters,
						onFiltersChange: handleFiltersChange,
					})}
				</WidgetContainer>
			</div>
		</>
	);
};

// Memoized DraggableWidget to prevent unnecessary re-renders during drag/resize
const DraggableWidget = React.memo(
	DraggableWidgetBase,
	(prevProps, nextProps) => {
		// Check what changed
		const positionChanged = !deepEqual(
			prevProps.layout.position,
			nextProps.layout.position,
		);
		const dataChanged = prevProps.widgetData !== nextProps.widgetData;
		const filtersChanged = !deepEqual(
			prevProps.dashboardFilters,
			nextProps.dashboardFilters,
		);
		const widgetConfigChanged =
			prevProps.widget.id !== nextProps.widget.id ||
			prevProps.widget.type !== nextProps.widget.type ||
			prevProps.widget.title !== nextProps.widget.title ||
			!deepEqual(prevProps.widget.settings, nextProps.widget.settings);
		const editModeChanged = prevProps.editMode !== nextProps.editMode;

		// Check all callback functions
		const onRefreshChanged = prevProps.onRefresh !== nextProps.onRefresh;
		const onFiltersChangeChanged =
			prevProps.onFiltersChange !== nextProps.onFiltersChange;
		const onDragStartChanged = prevProps.onDragStart !== nextProps.onDragStart;
		const onDragEndChanged = prevProps.onDragEnd !== nextProps.onDragEnd;

		// Decision logic
		const shouldRerender =
			positionChanged ||
			dataChanged ||
			filtersChanged ||
			widgetConfigChanged ||
			editModeChanged;

		if (shouldRerender) {
			return false; // Allow re-render
		}

		return true; // Skip re-render
	},
);

const WidgetGridComponent = forwardRef((props: WidgetGridProps, ref: any) => {
	const {
		widgets,
		editMode,
		onLayoutChange,
		onRemoveWidget,
		onWidgetSettings,
		onWidgetTitleChange,
		dashboardFilters,
		onWidgetFiltersChange,
		dashboardInitialized = true,
		dataAdapter,
		filterAdapter,
		renderWidget,
		renderFilterManager,
		renderFullscreenWidget,
		hideWidgetChrome = false,
		onExternalWidgetDrop,
	} = props;
	const colors = useThemeColors();
	const { t } = useTranslation();
	const isNativeMobile = Platform.isNative();
	const [isExternalDragOver, setIsExternalDragOver] = useState(false);

	// State for web mobile detection (responsive breakpoint)
	const [isWebMobile, setIsWebMobile] = useState(() => {
		if (typeof window !== 'undefined') {
			return window.innerWidth < WEB_MOBILE_BREAKPOINT;
		}
		return false;
	});

	// Resize listener for web mobile detection
	useEffect(() => {
		const handleWebMobileResize = () => {
			setIsWebMobile(window.innerWidth < WEB_MOBILE_BREAKPOINT);
		};

		window.addEventListener('resize', handleWebMobileResize);
		return () => {
			window.removeEventListener('resize', handleWebMobileResize);
		};
	}, []);

	const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
	const [widgetLayouts, setWidgetLayouts] = useState<
		Record<string, WidgetLayout>
	>({});
	const [containerWidth, setContainerWidth] = useState(0);
	const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
	const [dropTargetPosition, setDropTargetPosition] =
		useState<GridPosition | null>(null);
	const [affectedWidgetIds, setAffectedWidgetIds] = useState<string[]>([]);
	const [previewGridPosition, setPreviewGridPosition] =
		useState<GridPosition | null>(null);
	const [resizingWidgetId, setResizingWidgetId] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [reportBasedWidgetTypes, setReportBasedWidgetTypes] = useState<
		Set<string>
	>(new Set());
	const [widgetDefinitionsLoaded, setWidgetDefinitionsLoaded] =
		useState<boolean>(false);

	// Track subscriptions
	const widgetSubscriptionsRef = useRef<(() => void)[]>([]);
	const layoutsRef = useRef<Record<string, WidgetLayout>>({});
	const initializedWidgetIds = useRef<string>('');

	// Calculate grid dimensions based on container width. The container
	// fills the available viewport space (no artificial cap) so the
	// dashboard matches every other CRM page (orders, clients, etc.)
	// instead of being centered in a 1400px column on wide screens.
	const actualContainerWidth = containerWidth;
	const columnWidth =
		actualContainerWidth > 0
			? (actualContainerWidth - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS
			: 0;
	const cellSize = { width: columnWidth, height: GRID_ROW_HEIGHT };

	// Grid snap utility functions
	const snapToGridWidth = (pixelWidth: number): number => {
		const gridColumns = Math.max(
			1,
			Math.min(GRID_COLUMNS, Math.round(pixelWidth / (columnWidth + GRID_GAP))),
		);
		return gridColumns * columnWidth + (gridColumns - 1) * GRID_GAP;
	};

	const snapToGridHeight = (pixelHeight: number): number => {
		const gridRows = Math.max(
			1,
			Math.round(pixelHeight / (GRID_ROW_HEIGHT + GRID_GAP)),
		);
		return gridRows * GRID_ROW_HEIGHT + (gridRows - 1) * GRID_GAP;
	};

	const calculateGridDimensions = (width: number, height: number) => {
		const columns = Math.max(
			1,
			Math.min(GRID_COLUMNS, Math.round(width / (columnWidth + GRID_GAP))),
		);
		const rows = Math.max(1, Math.round(height / (GRID_ROW_HEIGHT + GRID_GAP)));
		return { columns, rows };
	};

	// Use stable reference for widgets to prevent unnecessary re-initializations
	const widgetIds = useMemo(
		() =>
			widgets
				.map((w) => w.id)
				.sort()
				.join(','),
		[widgets],
	);

	// Default list of known report-based widget types
	const defaultReportBasedWidgetTypes = new Set([
		// Sales & Revenue Widgets
		'sales-analysis',
		'item-performance-analysis',
		'sales-pipeline',
		'top-performers',
		'active-orders',
		'revenue-trends',
		'sales-by-product',
		'sales-by-company',
		'sales-by-product-company',
		'product-category-distribution',

		// Inventory Widgets
		'stock-levels',
		'low-stock-alerts',
		'inventory-valuation',
		'product-movement',
		'reorder-alerts',
		'reorder-alerts-insights',
		'at-risk-insights',

		// Client/Customer Widgets
		'new-clients',
		'top-clients-revenue',
		'client-payment-status',
		'customer-analytics',
		'customer-retention',
		'customer-lifetime-value',
		'new-customers',

		// Operational Widgets
		'pending-invoices',
		'system-alerts',

		// Analytics Widgets
		'kpi-overview',
		'kpi-details',
		'quick-stats',
		'goals-table',

		// Geographic Widgets
		'regional-sales',
		'territory-analysis',
		'shipping-zone-analysis',
		'shipping-zones',
		'market-penetration',
	]);

	// Load widget definitions on mount
	useEffect(() => {
		const loadWidgetDefinitions = async () => {
			try {
				const definitions = await dataAdapter.fetchWidgetDefinitions();
				if (definitions && definitions.length > 0) {
					const typeSet = new Set(definitions.map((def) => def.type));
					setReportBasedWidgetTypes(typeSet);
				} else {
					// Use default list if API returns empty
					setReportBasedWidgetTypes(defaultReportBasedWidgetTypes);
				}
				setWidgetDefinitionsLoaded(true);
			} catch (error) {
				// Fall back to default list of known report-based widgets
				setReportBasedWidgetTypes(defaultReportBasedWidgetTypes);
				setWidgetDefinitionsLoaded(true);
			}
		};

		loadWidgetDefinitions();
	}, []); // Run once on mount

	useEffect(() => {
		// Only initialize if widget IDs have actually changed or container width changed
		if (initializedWidgetIds.current !== widgetIds) {
			initializeLayouts();
			initializedWidgetIds.current = widgetIds;
		}
	}, [widgetIds, containerWidth]); // Remove position dependency to prevent loops

	useEffect(() => {
		const handleResize = () => {
			const newWidth = window.innerWidth - 48; // 48 for ScrollView padding (24px each side)
			setContainerWidth(newWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Subscribe to widget data store updates
	useEffect(() => {
		// Subscribe to store changes
		const unsubscribeStore = dataAdapter.subscribeToStateChanges(() => {
			const allData = dataAdapter.getAllWidgetData();
			const newData: Record<string, WidgetData> = {};
			allData.forEach((data, widgetId) => {
				newData[widgetId] = data;
			});
			setWidgetData(newData);
		});

		return () => {
			unsubscribeStore();
		};
	}, []);

	// Create a stable key that includes widget filter overrides
	const widgetFilterKey = useMemo(
		() =>
			widgets
				.map((w) => {
					const filterOverrides = w.filterConfig?.overrides || {};
					return `${w.id}:${JSON.stringify(filterOverrides)}`;
				})
				.join('|'),
		[widgets],
	);

	// Track previous widget filter states to detect individual changes
	const prevWidgetFiltersRef = useRef<Record<string, string>>({});

	useEffect(() => {
		// Only initialize widgets after definitions are loaded AND dashboard is initialized
		if (widgetDefinitionsLoaded && dashboardInitialized) {
			// Update widget definitions in store
			dataAdapter.setWidgetDefinitions(
				Array.from(reportBasedWidgetTypes).map((type) => {
					if (typeof type !== 'string') {
						// Error: Non-string widget type found:
						return {
							type: String(type),
							isReportBased: true,
						};
					}
					return {
						type,
						isReportBased: true,
					};
				}),
			);

			// Initialize all widgets in the store via the injected adapter.
			// CRM's adapter returns an unsubscribe callback; storefront's
			// adapter may return void — guard either shape.
			Promise.resolve(
				dataAdapter.batchInitializeWidgets(widgets, dashboardFilters ?? {}),
			).then((maybeUnsub) => {
				if (typeof maybeUnsub === 'function') {
					widgetSubscriptionsRef.current = [maybeUnsub];
				}
			});
		}

		return () => {
			// Clean up subscriptions
			widgetSubscriptionsRef.current.forEach((unsub) => {
				if (typeof unsub === 'function') {
					unsub();
				}
			});
			widgetSubscriptionsRef.current = [];
		};
	}, [
		widgetIds,
		widgetDefinitionsLoaded,
		dashboardFilters,
		dashboardInitialized,
	]); // Remove widgetFilterKey - handle individual widget changes separately

	// Handle individual widget filter changes without re-initializing all widgets
	useEffect(() => {
		if (!widgetDefinitionsLoaded || !dashboardInitialized) {
			return;
		}

		// Check each widget for filter changes
		widgets.forEach((widget) => {
			const filterOverrides = widget.filterConfig?.overrides || {};
			const inheritanceMode = widget.filterConfig?.inheritanceMode || 'inherit';
			const filterKey = `${inheritanceMode}:${JSON.stringify(filterOverrides)}`;
			const prevFilterKey = prevWidgetFiltersRef.current[widget.id];

			// Initialize or check for changes
			if (!prevFilterKey) {
				// First time seeing this widget, just track it
				prevWidgetFiltersRef.current[widget.id] = filterKey;
			} else if (prevFilterKey !== filterKey) {
				// Widget filter config changed - refresh only this widget
				// The widget object here already has the new filterConfig from props
				dataAdapter.refreshWidget(widget, dashboardFilters ?? {});
				// Update the tracking
				prevWidgetFiltersRef.current[widget.id] = filterKey;
			}
		});
	}, [
		widgets,
		dashboardFilters,
		widgetDefinitionsLoaded,
		dashboardInitialized,
	]);

	// Batch load all widget data - now delegated to the service
	const loadBatchWidgetData = async () => {
		// This method is kept for backward compatibility but now uses the service
		await dataAdapter.batchInitializeWidgets(widgets, dashboardFilters ?? {});
	};

	// Method to clear all widget data (used for dashboard refresh)
	const clearAllWidgetData = () => {
		// Use the service to refresh all widgets with their filter overrides
		dataAdapter.refreshAllWidgets(widgets, dashboardFilters ?? {});
	};

	const initializeLayouts = () => {
		const layouts: Record<string, WidgetLayout> = {};

		widgets.forEach((widget, index) => {
			const defaultWidth = getDefaultColumnSpan(widget.type);
			const defaultHeight = getDefaultRowSpan(widget.type);

			// Use saved position if available, otherwise use defaults
			if (
				widget.position &&
				((widget.position.col !== undefined &&
					widget.position.row !== undefined) ||
					(widget.position.x !== undefined && widget.position.y !== undefined))
			) {
				const savedPosition = {
					col: widget.position.col || widget.position.x || 0,
					row: widget.position.row || widget.position.y || 0,
					width: widget.position.width || widget.position.w || defaultWidth,
					height: widget.position.height || widget.position.h || defaultHeight,
				};
				layouts[widget.id] = {
					id: widget.id,
					position: savedPosition,
				};
			} else {
				// Specific positioning for default widgets
				if (widget.type === 'quick-stats') {
					layouts[widget.id] = {
						id: widget.id,
						position: { col: 0, row: 0, width: 4, height: 2 },
					};
				} else if (widget.type === 'sales-overview') {
					layouts[widget.id] = {
						id: widget.id,
						position: { col: 0, row: 2, width: 2, height: 3 },
					};
				} else if (widget.type === 'low-stock-alerts') {
					layouts[widget.id] = {
						id: widget.id,
						position: { col: 2, row: 2, width: 2, height: 3 },
					};
				} else if (widget.type === 'active-orders') {
					layouts[widget.id] = {
						id: widget.id,
						position: { col: 0, row: 5, width: 2, height: 3 },
					};
				} else if (widget.type === 'top-clients-revenue') {
					layouts[widget.id] = {
						id: widget.id,
						position: { col: 2, row: 5, width: 2, height: 3 },
					};
				} else {
					// For any additional widgets, find available position
					const availablePosition = findAvailablePosition(
						defaultWidth,
						defaultHeight,
						8,
					);
					layouts[widget.id] = {
						id: widget.id,
						position: availablePosition,
					};
				}
			}
		});

		setWidgetLayouts(layouts);
		layoutsRef.current = layouts;
	};

	const getDefaultColumnSpan = (widgetType: string): number => {
		switch (widgetType) {
			case 'quick-stats':
				return 4;
			case 'sales-overview':
			case 'low-stock-alerts':
			case 'active-orders':
			case 'top-clients-revenue':
				return 2;
			default:
				return 1;
		}
	};

	const getDefaultRowSpan = (widgetType: string): number => {
		switch (widgetType) {
			case 'quick-stats':
				return 2; // Keep compact for stats
			case 'sales-overview':
			case 'low-stock-alerts':
			case 'active-orders':
			case 'top-clients-revenue':
				return 3; // Standard height for content widgets
			default:
				return 2;
		}
	};

	// Sort widgets for mobile layout - by row first, then by column
	const sortWidgetsForMobile = useCallback(
		(widgetsToSort: Array<WidgetConfig>): Array<WidgetConfig> =>
			[...widgetsToSort].sort((a, b) => {
				const layoutA = widgetLayouts[a.id];
				const layoutB = widgetLayouts[b.id];
				if (layoutA == null || layoutB == null) {
					return 0;
				}

				// Sort by row first, then by column
				if (layoutA.position.row !== layoutB.position.row) {
					return layoutA.position.row - layoutB.position.row;
				}
				return layoutA.position.col - layoutB.position.col;
			}),
		[widgetLayouts],
	);

	const loadWidgetData = async (
		widget: WidgetConfig,
		force: boolean = false,
	) => {
		// Now delegated to the service
		await dataAdapter.refreshWidget(widget, dashboardFilters ?? {});
	};

	// Store widgets in a ref to avoid recreating handleRefresh
	const widgetsRef = useRef(widgets);
	useEffect(() => {
		widgetsRef.current = widgets;
	}, [widgets]);

	// Store dashboardFilters in a ref to avoid recreating handleRefresh
	const dashboardFiltersRef = useRef(dashboardFilters);
	useEffect(() => {
		dashboardFiltersRef.current = dashboardFilters;
	}, [dashboardFilters]);

	// Memoize handleRefresh to properly handle widget filter overrides
	const handleRefresh = useCallback((widgetId: string) => {
		const widget = widgetsRef.current.find((w) => w.id === widgetId);
		if (widget) {
			// Use the service to refresh the widget with its filter overrides
			dataAdapter.refreshWidget(widget, dashboardFiltersRef.current ?? {});
		}
	}, []);

	// Memoize handleRemove to prevent recreation
	const handleRemove = useCallback(
		(widgetId: string) => {
			onRemoveWidget(widgetId);
		},
		[onRemoveWidget],
	);

	// Memoize handleTitleChange to prevent recreation
	const handleTitleChange = useCallback(
		(widgetId: string, newTitle: string) => {
			onWidgetTitleChange?.(widgetId, newTitle);
		},
		[onWidgetTitleChange],
	);

	// Memoize handleFiltersChangeForWidget to prevent recreation
	const handleFiltersChangeForWidget = useCallback(
		(widgetId: string, filters: FilterState) => {
			onWidgetFiltersChange?.(widgetId, filters);
		},
		[onWidgetFiltersChange],
	);

	const gridPositionToPixels = (position: GridPosition) => ({
		x: position.col * (columnWidth + GRID_GAP),
		y: position.row * (GRID_ROW_HEIGHT + GRID_GAP),
		width: position.width * columnWidth + (position.width - 1) * GRID_GAP,
		height:
			position.height * GRID_ROW_HEIGHT + (position.height - 1) * GRID_GAP,
	});

	const pixelsToGridPosition = (
		x: number,
		y: number,
		width: number,
		height: number,
	): GridPosition => {
		const col = Math.max(
			0,
			Math.min(GRID_COLUMNS - 1, Math.round(x / (columnWidth + GRID_GAP))),
		);
		const row = Math.max(0, Math.round(y / (GRID_ROW_HEIGHT + GRID_GAP)));
		const gridWidth = Math.max(
			1,
			Math.min(
				GRID_COLUMNS - col,
				Math.round(width / (columnWidth + GRID_GAP)),
			),
		);
		const gridHeight = Math.max(
			1,
			Math.round(height / (GRID_ROW_HEIGHT + GRID_GAP)),
		);

		return { col, row, width: gridWidth, height: gridHeight };
	};

	// Helper function for drag operations - preserves widget size, only changes position
	const pixelsToGridPositionDragOnly = (
		x: number,
		y: number,
		originalWidth: number,
		originalHeight: number,
	): GridPosition => {
		// Use Math.floor for more responsive snapping - widget moves to new cell as soon as cursor enters it
		// Add small offset (GRID_GAP/2) to make the snapping feel more centered
		const col = Math.max(
			0,
			Math.min(
				GRID_COLUMNS - originalWidth,
				Math.floor((x + GRID_GAP / 2) / (columnWidth + GRID_GAP)),
			),
		);
		const row = Math.max(
			0,
			Math.floor((y + GRID_GAP / 2) / (GRID_ROW_HEIGHT + GRID_GAP)),
		);

		return {
			col,
			row,
			width: originalWidth, // Preserve original width
			height: originalHeight, // Preserve original height
		};
	};

	// Check if two positions overlap
	const checkCollision = (
		position: GridPosition,
		excludeId?: string,
	): boolean =>
		Object.entries(layoutsRef.current).some(([id, layout]) => {
			if (id === excludeId) {
				return false;
			}

			const pos = layout.position;
			return !(
				position.col + position.width <= pos.col ||
				position.col >= pos.col + pos.width ||
				position.row + position.height <= pos.row ||
				position.row >= pos.row + pos.height
			);
		});

	// Find available position for a widget
	const findAvailablePosition = (
		width: number,
		height: number,
		startRow: number = 0,
	): GridPosition => {
		for (let row = startRow; ; row++) {
			for (let col = 0; col <= GRID_COLUMNS - width; col++) {
				const position = { col, row, width, height };
				if (!checkCollision(position)) {
					return position;
				}
			}
		}
	};

	// Check collision against a specific layouts object
	const checkCollisionInLayouts = (
		position: GridPosition,
		excludeId: string,
		layouts: Record<string, WidgetLayout>,
	): boolean =>
		Object.entries(layouts).some(([id, layout]) => {
			if (id === excludeId) {
				return false;
			}

			const pos = layout.position;
			return !(
				position.col + position.width <= pos.col ||
				position.col >= pos.col + pos.width ||
				position.row + position.height <= pos.row ||
				position.row >= pos.row + pos.height
			);
		});

	// Get widgets that would be affected by the new position
	const getAffectedWidgets = (newPosition: GridPosition, excludeId: string) =>
		widgets.filter((widget) => {
			if (widget.id === excludeId) {
				return false;
			}

			const layout = layoutsRef.current[widget.id];
			if (!layout) {
				return false;
			}

			const pos = layout.position;
			// Check if widgets overlap with the new position
			return !(
				newPosition.col + newPosition.width <= pos.col ||
				newPosition.col >= pos.col + pos.width ||
				newPosition.row + newPosition.height <= pos.row ||
				newPosition.row >= pos.row + pos.height
			);
		});

	// Update widget configs with positions in both formats for compatibility
	const updateWidgetConfigs = useCallback(() => {
		const updatedWidgets = widgets.map((widget) => {
			const layout = layoutsRef.current[widget.id] || widgetLayouts[widget.id];
			if (!layout) {
				return widget;
			}

			return {
				...widget,
				position: {
					// Keep both old and new formats for full compatibility
					x: layout.position.col,
					y: layout.position.row,
					w: layout.position.width,
					h: layout.position.height,
					col: layout.position.col,
					row: layout.position.row,
					width: layout.position.width,
					height: layout.position.height,
				},
				size: {
					width: layout.position.width,
					height: layout.position.height,
				},
			};
		});

		onLayoutChange(updatedWidgets);
	}, [widgets, widgetLayouts, onLayoutChange]);

	// Smart reflow with directional pushing
	const smartReflow = (
		resizedWidgetId: string,
		newPosition: GridPosition,
		oldPosition: GridPosition,
	) => {
		const widthChange = newPosition.width - oldPosition.width;
		const heightChange = newPosition.height - oldPosition.height;

		const newLayouts = { ...layoutsRef.current };

		// Update the resized widget position
		const resizedLayout = newLayouts[resizedWidgetId];
		newLayouts[resizedWidgetId] = {
			...resizedLayout,
			id: resizedLayout?.id ?? resizedWidgetId,
			position: newPosition,
		};

		// Handle expansion scenarios - push affected widgets away
		if (widthChange > 0 || heightChange > 0) {
			const affectedWidgets = getAffectedWidgets(newPosition, resizedWidgetId);

			// Determine primary expansion direction
			if (Math.abs(widthChange) >= Math.abs(heightChange)) {
				// Horizontal expansion is primary
				if (widthChange > 0) {
					pushWidgetsRight(
						affectedWidgets,
						newPosition,
						newLayouts,
						resizedWidgetId,
					);
				}
			} else {
				// Vertical expansion is primary
				if (heightChange > 0) {
					pushWidgetsDown(affectedWidgets, newPosition, newLayouts);
				}
			}
		}

		// Handle shrinking - fill gaps
		if (widthChange < 0 || heightChange < 0) {
			if (widthChange < 0) {
				pushWidgetsLeft(newLayouts, resizedWidgetId);
			}
			if (heightChange < 0) {
				pushWidgetsUp(newLayouts, resizedWidgetId);
			}
		}

		// Always compact the layout
		compactLayout(newLayouts, resizedWidgetId);

		setWidgetLayouts(newLayouts);
		layoutsRef.current = newLayouts;
		updateWidgetConfigs();
	};

	// Push widgets to the right
	const pushWidgetsRight = (
		affectedWidgets: WidgetConfig[],
		resizedPosition: GridPosition,
		layouts: Record<string, WidgetLayout>,
		resizingWidgetId?: string,
	) => {
		const rightBoundary = resizedPosition.col + resizedPosition.width;
		const movedWidgets = new Set<string>();

		affectedWidgets.forEach((widget) => {
			const layout = layouts[widget.id];
			if (!layout) {
				return;
			}

			const currentPos = layout.position;

			// If widget overlaps horizontally, try to push it to the right
			if (currentPos.col < rightBoundary) {
				const newCol = Math.min(rightBoundary, GRID_COLUMNS - currentPos.width);

				if (newCol + currentPos.width > GRID_COLUMNS) {
					// Can't fit to the right, move down
					const newRow = resizedPosition.row + resizedPosition.height;
					layouts[widget.id] = {
						...layout,
						position: { ...currentPos, row: newRow, col: 0 },
					};
				} else {
					// Can fit to the right
					layouts[widget.id] = {
						...layout,
						position: { ...currentPos, col: newCol },
					};
				}

				movedWidgets.add(widget.id);
			}
		});

		// Resolve cascading collisions
		if (movedWidgets.size > 0) {
			resolveCascadingCollisions(layouts, movedWidgets);
		}
	};

	// Push widgets down
	const pushWidgetsDown = (
		affectedWidgets: WidgetConfig[],
		resizedPosition: GridPosition,
		layouts: Record<string, WidgetLayout>,
	) => {
		const bottomBoundary = resizedPosition.row + resizedPosition.height;
		const movedWidgets = new Set<string>();

		affectedWidgets.forEach((widget) => {
			const layout = layouts[widget.id];
			if (!layout) {
				return;
			}

			const currentPos = layout.position;

			// If widget overlaps vertically, push it down
			if (currentPos.row < bottomBoundary) {
				const newRow = bottomBoundary;

				layouts[widget.id] = {
					...layout,
					position: { ...currentPos, row: newRow },
				};

				movedWidgets.add(widget.id);
			}
		});

		// Resolve cascading collisions
		if (movedWidgets.size > 0) {
			resolveCascadingCollisions(layouts, movedWidgets);
		}
	};

	// Push widgets left when space becomes available
	const pushWidgetsLeft = (
		layouts: Record<string, WidgetLayout>,
		resizedWidgetId: string,
	) => {
		const sortedWidgets = widgets
			.filter((w) => w.id !== resizedWidgetId)
			.sort((a, b) => {
				const layoutA = layouts[a.id];
				const layoutB = layouts[b.id];
				if (!layoutA || !layoutB) {
					return 0;
				}

				// Sort by row first, then by column
				if (layoutA.position.row === layoutB.position.row) {
					return layoutA.position.col - layoutB.position.col;
				}
				return layoutA.position.row - layoutB.position.row;
			});

		const movedWidgets = new Set<string>();

		sortedWidgets.forEach((widget) => {
			const layout = layouts[widget.id];
			if (!layout) {
				return;
			}

			const currentPos = layout.position;

			// Try to move widget left as much as possible
			for (let col = 0; col < currentPos.col; col++) {
				const testPosition = { ...currentPos, col };

				if (!checkCollisionInLayouts(testPosition, widget.id, layouts)) {
					layouts[widget.id] = {
						...layout,
						position: testPosition,
					};
					movedWidgets.add(widget.id);
					break;
				}
			}
		});

		if (movedWidgets.size > 0) {
			resolveCascadingCollisions(layouts, movedWidgets);
		}
	};

	// Push widgets up when space becomes available
	const pushWidgetsUp = (
		layouts: Record<string, WidgetLayout>,
		resizedWidgetId: string,
	) => {
		const sortedWidgets = widgets
			.filter((w) => w.id !== resizedWidgetId)
			.sort((a, b) => {
				const layoutA = layouts[a.id];
				const layoutB = layouts[b.id];
				if (!layoutA || !layoutB) {
					return 0;
				}

				// Sort by row first, then by column
				if (layoutA.position.row === layoutB.position.row) {
					return layoutA.position.col - layoutB.position.col;
				}
				return layoutA.position.row - layoutB.position.row;
			});

		const movedWidgets = new Set<string>();

		sortedWidgets.forEach((widget) => {
			const layout = layouts[widget.id];
			if (!layout) {
				return;
			}

			const currentPos = layout.position;

			// Try to move widget up as much as possible
			for (let row = 0; row < currentPos.row; row++) {
				const testPosition = { ...currentPos, row };

				if (!checkCollisionInLayouts(testPosition, widget.id, layouts)) {
					layouts[widget.id] = {
						...layout,
						position: testPosition,
					};
					movedWidgets.add(widget.id);
					break;
				}
			}
		});

		if (movedWidgets.size > 0) {
			resolveCascadingCollisions(layouts, movedWidgets);
		}
	};

	// Compact layout by moving widgets up/left when space is available
	const compactLayout = (
		layouts: Record<string, WidgetLayout>,
		excludeId: string,
	) => {
		const sortedWidgets = widgets
			.filter((w) => w.id !== excludeId)
			.sort((a, b) => {
				const layoutA = layouts[a.id];
				const layoutB = layouts[b.id];
				if (!layoutA || !layoutB) {
					return 0;
				}

				// Sort by row first, then by column
				if (layoutA.position.row === layoutB.position.row) {
					return layoutA.position.col - layoutB.position.col;
				}
				return layoutA.position.row - layoutB.position.row;
			});

		sortedWidgets.forEach((widget) => {
			const layout = layouts[widget.id];
			if (!layout) {
				return;
			}

			const currentPos = layout.position;

			// Try to move widget up as much as possible
			for (let row = 0; row < currentPos.row; row++) {
				const testPosition = { ...currentPos, row };

				if (!checkCollisionInLayouts(testPosition, widget.id, layouts)) {
					layouts[widget.id] = {
						...layout,
						position: testPosition,
					};
					break;
				}
			}
		});
	};

	// Find all widgets that collide with a specific widget's position
	const findCollisionsWithWidget = (
		widgetId: string,
		layouts: Record<string, WidgetLayout>,
	): string[] => {
		const widget = layouts[widgetId];
		if (!widget) {
			return [];
		}

		const collidingIds: string[] = [];
		const widgetPos = widget.position;

		Object.entries(layouts).forEach(([id, layout]) => {
			if (id === widgetId) {
				return;
			}

			const pos = layout.position;
			// Check if widgets overlap
			const hasCollision = !(
				widgetPos.col + widgetPos.width <= pos.col ||
				widgetPos.col >= pos.col + pos.width ||
				widgetPos.row + widgetPos.height <= pos.row ||
				widgetPos.row >= pos.row + pos.height
			);

			if (hasCollision) {
				collidingIds.push(id);
			}
		});

		return collidingIds;
	};

	// Resolve cascading collisions recursively
	const resolveCascadingCollisions = (
		layouts: Record<string, WidgetLayout>,
		movedWidgetIds: Set<string>,
		maxIterations: number = 10,
	): void => {
		if (maxIterations <= 0) {
			return;
		}

		let hasNewCollisions = false;
		const newlyMovedWidgets = new Set<string>();

		// Check each moved widget for new collisions
		movedWidgetIds.forEach((widgetId) => {
			const collidingIds = findCollisionsWithWidget(widgetId, layouts);

			if (collidingIds.length > 0) {
				hasNewCollisions = true;

				// Move colliding widgets to safe positions
				collidingIds.forEach((collidingId) => {
					const collidingLayout = layouts[collidingId];
					if (!collidingLayout) {
						return;
					}

					const currentPos = collidingLayout.position;
					const triggeringWidget = layouts[widgetId];
					if (!triggeringWidget) {
						return;
					}

					// Determine direction to move the colliding widget
					const triggerPos = triggeringWidget.position;

					// Try to move right first, then down if right doesn't work
					let newPosition: GridPosition;

					// Calculate right boundary of triggering widget
					const rightBoundary = triggerPos.col + triggerPos.width;
					const newCol = rightBoundary;

					if (newCol + currentPos.width <= GRID_COLUMNS) {
						// Move right
						newPosition = { ...currentPos, col: newCol };
					} else {
						// Move down if can't go right
						const bottomBoundary = triggerPos.row + triggerPos.height;
						newPosition = { ...currentPos, row: bottomBoundary, col: 0 };
					}

					// Update position
					layouts[collidingId] = {
						...collidingLayout,
						position: newPosition,
					};

					newlyMovedWidgets.add(collidingId);
				});
			}
		});

		// If we found new collisions, recursively check again
		if (hasNewCollisions && newlyMovedWidgets.size > 0) {
			resolveCascadingCollisions(layouts, newlyMovedWidgets, maxIterations - 1);
		}
	};

	// Widget types that are handled by ReportWidgetLoader (no separate data fetching needed)
	const reportBasedWidgetTypesList = [
		'sales-overview',
		'revenue-trends',
		'item-performance-analysis',
		'sales-pipeline',
		'top-performers',
		'stock-levels',
		'low-stock-alerts',
		'inventory-valuation',
		'product-movement',
		'new-clients',
		'top-clients-revenue',
		'client-payment-status',
		'customer-analytics',
		'customer-retention',
		'customer-lifetime-value',
		'new-customers',
		'pending-invoices',
		'active-orders',
		'system-alerts',
		'kpi-overview',
		'quick-stats',
		'regional-sales',
		'territory-analysis',
		'shipping-zone-analysis',
		'market-penetration',
		'sales-by-product',
		'product-category-distribution',
	];

	// Use stable reference for widgets to prevent unnecessary re-initializations
	const widgetIdsKey = useMemo(
		() =>
			widgets
				.map((w) => w.id)
				.sort()
				.join(','),
		[widgets],
	);

	// Initialize widget layouts with support for both position formats
	useEffect(() => {
		// Force re-initialization when:
		// 1. Widget IDs change (widgets added/removed)
		// 2. Widget positions fundamentally change (e.g., from cancel operation)
		const shouldReinitialize =
			initializedWidgetIds.current !== widgetIdsKey ||
			// Check if any widget position has changed from what we have in layoutsRef
			widgets.some((widget) => {
				const currentLayout = layoutsRef.current[widget.id];
				if (!currentLayout || !widget.position) {
					return false;
				}

				// Compare saved position with current layout
				const savedCol = widget.position.col ?? widget.position.x ?? 0;
				const savedRow = widget.position.row ?? widget.position.y ?? 0;
				const savedWidth =
					widget.position.width ??
					widget.position.w ??
					getSizeSpan(widget.size, 'width') ??
					getDefaultColumnSpan(widget.type);
				const savedHeight =
					widget.position.height ??
					widget.position.h ??
					getSizeSpan(widget.size, 'height') ??
					getDefaultRowSpan(widget.type);

				return (
					currentLayout.position.col !== savedCol ||
					currentLayout.position.row !== savedRow ||
					currentLayout.position.width !== savedWidth ||
					currentLayout.position.height !== savedHeight
				);
			});

		// Only initialize if widget IDs have actually changed or positions need reset
		if (shouldReinitialize) {
			const newLayouts: Record<string, WidgetLayout> = {};
			let currentRow = 0;
			let currentCol = 0;

			widgets.forEach((widget) => {
				const defaultWidth = getDefaultColumnSpan(widget.type);
				const defaultHeight = getDefaultRowSpan(widget.type);

				// Use saved position if available, supporting both old and new formats
				if (
					widget.position &&
					((widget.position.col !== undefined &&
						widget.position.row !== undefined) ||
						(widget.position.x !== undefined &&
							widget.position.y !== undefined))
				) {
					const savedPosition = {
						col: widget.position.col ?? widget.position.x ?? 0,
						row: widget.position.row ?? widget.position.y ?? 0,
						width:
							widget.position.width ??
							widget.position.w ??
							getSizeSpan(widget.size, 'width') ??
							defaultWidth,
						height:
							widget.position.height ??
							widget.position.h ??
							getSizeSpan(widget.size, 'height') ??
							defaultHeight,
					};
					newLayouts[widget.id] = {
						id: widget.id,
						position: savedPosition,
					};
				} else {
					// Auto-layout widgets that don't have positions
					const width = getSizeSpan(widget.size, 'width') ?? defaultWidth;
					const height = getSizeSpan(widget.size, 'height') ?? defaultHeight;

					if (currentCol + width > GRID_COLUMNS) {
						currentCol = 0;
						currentRow += height; // Move to next row
					}

					newLayouts[widget.id] = {
						id: widget.id,
						position: {
							col: currentCol,
							row: currentRow,
							width,
							height,
						},
					};

					currentCol += width;
				}
			});

			setWidgetLayouts(newLayouts);
			layoutsRef.current = newLayouts;
			initializedWidgetIds.current = widgetIdsKey;
		}
	}, [widgetIdsKey, widgets]);

	// Default size functions are already defined above

	// Smart drag reposition using position-first algorithm
	const handleSmartDragReposition = (
		draggedWidgetId: string,
		desiredPosition: GridPosition,
		layouts: Record<string, WidgetLayout>,
	) => {
		// Lock dragged widget at exact desired position
		const draggedLayout = layouts[draggedWidgetId];
		layouts[draggedWidgetId] = {
			...draggedLayout,
			id: draggedLayout?.id ?? draggedWidgetId,
			position: desiredPosition,
		};

		// Detect position conflicts
		const conflictingWidgets: string[] = [];
		Object.entries(layouts).forEach(([id, layout]) => {
			if (id === draggedWidgetId) {
				return;
			}

			const hasConflict = !(
				layout.position.col + layout.position.width <= desiredPosition.col ||
				layout.position.col >= desiredPosition.col + desiredPosition.width ||
				layout.position.row + layout.position.height <= desiredPosition.row ||
				layout.position.row >= desiredPosition.row + desiredPosition.height
			);

			if (hasConflict) {
				conflictingWidgets.push(id);
			}
		});

		// Move only conflicting widgets to new positions
		const movedWidgets: string[] = [];

		conflictingWidgets.forEach((widgetId) => {
			const widget = layouts[widgetId];
			if (!widget) {
				return;
			}

			// Find next available position
			const newPosition = findNextAvailablePosition(
				widget.position,
				layouts,
				desiredPosition,
			);

			layouts[widgetId] = {
				...widget,
				position: newPosition,
			};

			movedWidgets.push(widgetId);
		});

		// Resolve cascading collisions
		if (movedWidgets.length > 0) {
			const movedWidgetSet = new Set(movedWidgets);
			resolveCascadingCollisions(layouts, movedWidgetSet);
		}

		return {
			success: true,
			movedWidgets,
			draggedWidgetKeptPosition: true,
			conflictingWidgets: conflictingWidgets.length,
			nonConflictingWidgets: widgets.length - conflictingWidgets.length - 1,
		};
	};

	// Find next available position for a widget
	const findNextAvailablePosition = (
		currentPosition: GridPosition,
		layouts: Record<string, WidgetLayout>,
		avoidPosition: GridPosition,
	): GridPosition => {
		const { width, height } = currentPosition;

		// Try positions to the right first (same row)
		for (
			let col = avoidPosition.col + avoidPosition.width;
			col + width <= GRID_COLUMNS;
			col++
		) {
			const testPosition = { ...currentPosition, col };
			if (!checkCollisionInLayouts(testPosition, '', layouts)) {
				return testPosition;
			}
		}

		// If can't fit right, move down to next row
		const newRow = avoidPosition.row + avoidPosition.height;
		const newPosition = { ...currentPosition, row: newRow, col: 0 };
		return newPosition;
	};

	// Handle drag end with smart repositioning
	const handleDragEnd = useCallback(
		(widgetId: string, x: number, y: number) => {
			const layout = layoutsRef.current[widgetId];
			if (!layout) {
				return;
			}

			// For drag operations, preserve original widget dimensions and only change position
			const newPosition = pixelsToGridPositionDragOnly(
				x,
				y,
				layout.position.width,
				layout.position.height,
			);
			// Use smart drag reposition algorithm for intelligent repositioning
			const newLayouts = { ...layoutsRef.current };
			const repositionResult = handleSmartDragReposition(
				widgetId,
				newPosition,
				newLayouts,
			);

			if (repositionResult.success) {
				setWidgetLayouts(newLayouts);
				layoutsRef.current = newLayouts;
				// Update widget configs to notify parent about layout changes
				// This enables the save button, just like resize does
				updateWidgetConfigs();
			} else {
				// Fallback: keep widget at original position
				// No need to update layouts since position didn't change
			}

			// Clean up drag state
			setDraggedWidgetId(null);
			setDropTargetPosition(null);
			setAffectedWidgetIds([]);
			setPreviewGridPosition(null);
		},
		[pixelsToGridPositionDragOnly, updateWidgetConfigs],
	);

	// Handle drag move for live preview
	const handleDragMove = useCallback(
		(widgetId: string, previewPosition: GridPosition) => {
			// Only update the preview position, don't move other widgets
			// The actual repositioning will happen in handleDragEnd
			// Find widgets that would be affected (for visual feedback only)
			// const affectedWidgets = getAffectedWidgets(previewPosition, widgetId);
			// Commenting out to prevent re-renders and API calls during drag
			// setAffectedWidgetIds(affectedWidgets.map(w => w.id));
			// The preview position is already being set by the DraggableWidget component
			// No need to actually move any widgets here
		},
		[],
	);

	// Handle resize end
	const handleResizeEnd = useCallback(
		(widgetId: string, width: number, height: number) => {
			const layout = layoutsRef.current[widgetId];
			if (!layout) {
				return;
			}

			const oldPosition = layout.position;
			const pixels = gridPositionToPixels(layout.position);
			const newPosition = pixelsToGridPosition(
				pixels.x,
				pixels.y,
				width,
				height,
			);

			// Use smart repositioning for resize
			smartReflow(widgetId, newPosition, oldPosition);

			// Clean up resize state
			setResizingWidgetId(null);
			setAffectedWidgetIds([]);
			setPreviewGridPosition(null);
		},
		[gridPositionToPixels, pixelsToGridPosition, smartReflow],
	);

	// Handle resize move for live preview
	const handleResizeMove = useCallback(
		(
			widgetId: string,
			previewPosition: GridPosition,
			oldPosition: GridPosition,
		) => {
			// Only update the preview for visual feedback, don't move other widgets
			// The actual repositioning will happen in handleResizeEnd
			// Find widgets that would be affected (for visual feedback only)
			// const affectedWidgets = getAffectedWidgets(previewPosition, widgetId);
			// Commenting out to prevent re-renders during resize
			// setAffectedWidgetIds(affectedWidgets.map(w => w.id));
			// The preview position is already being shown by the resize preview indicator
			// No need to actually move any widgets here
		},
		[],
	);

	// Handle widget drop/reorder (for drop on other widgets)
	const handleWidgetDrop = useCallback(
		(draggedId: string, targetId: string) => {
			const draggedLayout = widgetLayouts[draggedId];
			const targetLayout = widgetLayouts[targetId];

			if (!draggedLayout || !targetLayout) {
				return;
			}

			// Use target widget's position as desired position
			const newLayouts = { ...widgetLayouts };
			const desiredPosition = targetLayout.position;

			const repositionResult = handleSmartDragReposition(
				draggedId,
				desiredPosition,
				newLayouts,
			);

			if (repositionResult.success) {
				setWidgetLayouts(newLayouts);
				layoutsRef.current = newLayouts;
				// Update widget configs to notify parent about layout changes
				// This enables the save button, just like resize does
				updateWidgetConfigs();
			}
		},
		[widgetLayouts, widgets, updateWidgetConfigs],
	);

	// Monitor container width. Re-measures when `editMode` toggles because
	// the grid container (which carries `containerRef`) only mounts on the
	// desktop/edit-mode render path — on mobile VIEW mode it isn't in the
	// tree, so entering edit mode on a phone needs a fresh measurement or
	// columnWidth would stay 0 and widgets would render with no width.
	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};

		updateWidth();
		window.addEventListener('resize', updateWidth);

		return () => {
			window.removeEventListener('resize', updateWidth);
		};
	}, [editMode]);

	// Duplicate function removed - using the original loadWidgetData

	// Batch load all widget data - duplicate removed, using original
	const loadBatchWidgetDataDuplicate = async () => {
		// Separate report-based and non-report widgets
		const reportWidgets: WidgetConfig[] = [];
		const nonReportWidgets: WidgetConfig[] = [];

		widgets.forEach((widget) => {
			const currentData = widgetData[widget.id];
			// Only load if widget has no data or has an error
			// Skip widgets that are already loading to prevent duplicate requests
			if (
				!currentData ||
				(!currentData.data && !currentData.loading) ||
				currentData.error
			) {
				if (reportBasedWidgetTypesList.includes(widget.type)) {
					reportWidgets.push(widget);
				} else {
					nonReportWidgets.push(widget);
				}
			}
		});

		// Mark report-based widgets with special data
		if (reportWidgets.length > 0) {
			const reportWidgetState: Record<string, any> = {};
			reportWidgets.forEach((widget) => {
				reportWidgetState[widget.id] = {
					loading: false,
					data: { isReportBased: true },
					lastUpdated: new Date(),
				};
			});
			setWidgetData((prev) => ({ ...prev, ...reportWidgetState }));
		}

		if (nonReportWidgets.length === 0) {
			return;
		}

		// Mark all widgets as loading
		const loadingState: Record<string, any> = {};
		nonReportWidgets.forEach((widget) => {
			loadingState[widget.id] = { loading: true, data: null };
		});
		setWidgetData((prev) => ({ ...prev, ...loadingState }));

		try {
			// Fetch all widget data in a single batch request
			const dataMap = await dataAdapter.fetchBatchWidgetData(nonReportWidgets);

			// Update widget data state with batch results
			const newDataState: Record<string, any> = {};
			nonReportWidgets.forEach((widget) => {
				const data =
					dataMap instanceof Map ? dataMap.get(widget.id) : dataMap[widget.id];
				// Don't treat null/undefined data as an error - some widgets may legitimately have no data
				newDataState[widget.id] = {
					loading: false,
					data: data || null,
					error: null,
					lastUpdated: new Date(),
				};
			});

			setWidgetData((prev) => ({ ...prev, ...newDataState }));
		} catch (error) {
			// Mark all widgets as errored
			const errorState: Record<string, any> = {};
			nonReportWidgets.forEach((widget) => {
				errorState[widget.id] = {
					loading: false,
					error: 'Failed to load widget data',
					lastUpdated: new Date(),
				};
			});
			setWidgetData((prev) => ({ ...prev, ...errorState }));
		}
	};

	// Duplicate clearAllWidgetData removed - using the original

	// Expose methods to parent via ref
	useImperativeHandle(ref, () => ({
		clearAllWidgetData,
		refreshWidget: handleRefresh,
	}));

	// Calculate grid rows needed
	const maxRow = useMemo(
		() =>
			Object.values(widgetLayouts).reduce(
				(max, layout) =>
					Math.max(max, layout.position.row + layout.position.height),
				0,
			),
		[widgetLayouts],
	);

	// Add 2 extra rows in edit mode to show available space for adding/dragging widgets
	const gridRows = editMode ? Math.max(12, maxRow + 2) : Math.max(12, maxRow);
	const minHeight = gridRows * GRID_ROW_HEIGHT + (gridRows - 1) * GRID_GAP;

	// True iff the in-flight drag carries a widget tile from the picker.
	// Other drags (text selections, files, etc.) are ignored so we don't
	// hijack the cursor on every random dragover.
	const isExternalWidgetDrag = useCallback(
		(e: React.DragEvent<HTMLDivElement>) =>
			onExternalWidgetDrop != null &&
			e.dataTransfer.types.includes(EXTERNAL_WIDGET_MIME),
		[onExternalWidgetDrop],
	);

	// Parse "<w>x<h>" from the dimensions slot, with a sensible fallback
	// when the source dragged something without it (older browsers, manual
	// scripted drags, etc.).
	const parseExternalDims = useCallback(
		(e: React.DragEvent<HTMLDivElement>): { width: number; height: number } => {
			const raw = e.dataTransfer.getData(EXTERNAL_WIDGET_DIMS_MIME);
			const match = /^(\d+)x(\d+)$/.exec(raw);
			if (match?.[1] != null && match[2] != null) {
				const w = Math.max(1, Math.min(GRID_COLUMNS, parseInt(match[1], 10)));
				const h = Math.max(1, parseInt(match[2], 10));
				return { width: w, height: h };
			}
			return { width: 2, height: 2 };
		},
		[],
	);

	// Convert the pointer position from client coords to grid-container
	// coords, then snap to a cell. Mirrors the math the internal drag uses
	// in `handleDragEnd`.
	const dragEventToGridPosition = useCallback(
		(
			e: React.DragEvent<HTMLDivElement>,
			width: number,
			height: number,
		): GridPosition | null => {
			const container = containerRef.current;
			if (container == null) {
				return null;
			}
			const rect = container.getBoundingClientRect();
			// The pointer slot inside the dragged tile isn't at the tile's
			// top-left corner; aiming for cell where the cursor *would* land
			// the widget's top-left feels right. Subtracting half-cell
			// centers the cursor over the widget which is what users expect.
			const cellW = columnWidth + GRID_GAP;
			const cellH = GRID_ROW_HEIGHT + GRID_GAP;
			const x = e.clientX - rect.left - (cellW * width) / 2 + cellW / 2;
			const y = e.clientY - rect.top - (cellH * height) / 2 + cellH / 2;
			return pixelsToGridPositionDragOnly(x, y, width, height);
		},
		[columnWidth],
	);

	// Compute which existing widgets would be pushed if the dragged tile
	// landed at `target`. Mirrors the conflict scan in
	// `handleSmartDragReposition` so the visual highlight during the drag
	// matches what the drop will actually do.
	const conflictingIdsForExternalDrop = useCallback(
		(target: GridPosition): string[] => {
			const out: string[] = [];
			Object.entries(widgetLayouts).forEach(([id, layout]) => {
				const p = layout.position;
				const overlap = !(
					p.col + p.width <= target.col ||
					p.col >= target.col + target.width ||
					p.row + p.height <= target.row ||
					p.row >= target.row + target.height
				);
				if (overlap) {
					out.push(id);
				}
			});
			return out;
		},
		[widgetLayouts],
	);

	const handleExternalDragOver = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			if (!isExternalWidgetDrag(e)) {
				return;
			}
			// preventDefault is what tells the browser this is a valid drop
			// target — without it the drop event never fires.
			e.preventDefault();
			e.dataTransfer.dropEffect = 'copy';
			if (!isExternalDragOver) {
				setIsExternalDragOver(true);
			}

			const { width, height } = parseExternalDims(e);
			const target = dragEventToGridPosition(e, width, height);
			if (target == null) {
				return;
			}
			// Drive the same preview-box state the internal drag uses so
			// the user sees the green rectangle land in the candidate cell.
			setPreviewGridPosition(target);
			setAffectedWidgetIds(conflictingIdsForExternalDrop(target));
		},
		[
			isExternalWidgetDrag,
			isExternalDragOver,
			parseExternalDims,
			dragEventToGridPosition,
			conflictingIdsForExternalDrop,
		],
	);

	const handleExternalDragLeave = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			// dragleave fires when crossing into a child element too;
			// only clear when we've actually left the container.
			if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
				return;
			}
			setIsExternalDragOver(false);
			setPreviewGridPosition(null);
			setAffectedWidgetIds([]);
		},
		[],
	);

	const handleExternalDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			if (!isExternalWidgetDrag(e)) {
				return;
			}
			e.preventDefault();
			setIsExternalDragOver(false);

			const widgetType =
				e.dataTransfer.getData(EXTERNAL_WIDGET_MIME) ||
				e.dataTransfer.getData('text/plain');
			const { width, height } = parseExternalDims(e);
			const target = dragEventToGridPosition(e, width, height);

			// Always clear preview state, even on a no-op drop.
			setPreviewGridPosition(null);
			setAffectedWidgetIds([]);

			if (widgetType === '' || target == null || onExternalWidgetDrop == null) {
				return;
			}

			// Reflow existing widgets out of the way using the same engine
			// the internal drag uses. Inject a synthetic entry for the
			// incoming external widget so handleSmartDragReposition's
			// conflict scan sees it as occupying the target cell, then drop
			// the synthetic entry — only real widgets get persisted.
			const reflowedLayouts: Record<string, WidgetLayout> = {
				...layoutsRef.current,
				[EXTERNAL_DRAG_PREVIEW_ID]: {
					id: EXTERNAL_DRAG_PREVIEW_ID,
					position: target,
				},
			};
			handleSmartDragReposition(
				EXTERNAL_DRAG_PREVIEW_ID,
				target,
				reflowedLayouts,
			);
			delete reflowedLayouts[EXTERNAL_DRAG_PREVIEW_ID];

			// Translate WidgetLayout → WidgetConfig (mirrors updateWidgetConfigs)
			// so the parent has a complete widgets array with reflowed
			// positions and can append the new widget without losing them.
			// Doing the merge here — instead of calling updateWidgetConfigs()
			// + onExternalWidgetDrop separately — avoids a render-batch race
			// where the parent's handleAddWidget's closure would otherwise
			// re-base off the pre-reflow widgets and silently undo it.
			const reflowedWidgets: WidgetConfig[] = widgets.map((w) => {
				const layout = reflowedLayouts[w.id];
				if (layout == null) {
					return w;
				}
				return {
					...w,
					position: {
						x: layout.position.col,
						y: layout.position.row,
						w: layout.position.width,
						h: layout.position.height,
						col: layout.position.col,
						row: layout.position.row,
						width: layout.position.width,
						height: layout.position.height,
					},
					size: {
						width: layout.position.width,
						height: layout.position.height,
					},
				};
			});

			// Apply the reflow visually right away. The parent's prop update
			// (with the new widget appended) will re-init layouts shortly
			// after — the snapshot we just set keeps the "during" frame
			// stable so widgets don't appear to teleport while React commits.
			setWidgetLayouts(reflowedLayouts);
			layoutsRef.current = reflowedLayouts;

			onExternalWidgetDrop(widgetType, target, reflowedWidgets);
		},
		[
			widgets,
			isExternalWidgetDrag,
			onExternalWidgetDrop,
			parseExternalDims,
			dragEventToGridPosition,
		],
	);

	// Mobile VIEW layout - read-only vertical stack (native mobile or web
	// mobile viewport). In EDIT mode we fall through to the same draggable
	// grid the desktop uses so the user can add/remove/rename widgets and
	// drag-reposition / resize the layout on touch (PD-14128). The grid is
	// a fixed-column grid so it renders compact on a small phone — that's
	// acceptable per the feature owner, who targeted big-screen phones.
	if ((isNativeMobile || isWebMobile) && !editMode) {
		const sortedWidgets = sortWidgetsForMobile(widgets);

		return (
			<div className="w-full px-4 pb-4">
				<div className="flex flex-col gap-4">
					{sortedWidgets.map((widget) => {
						const layout = widgetLayouts[widget.id];
						if (layout == null) {
							return null;
						}

						const widgetMinHeight =
							layout.position.height * GRID_ROW_HEIGHT +
							(layout.position.height - 1) * GRID_GAP;
						const widgetDataItem = widgetData[widget.id];

						return (
							<div
								key={widget.id}
								style={{
									height: `${Math.max(MIN_WIDGET_HEIGHT, widgetMinHeight)}px`,
								}}
								className="w-full"
							>
								<WidgetContainer
									config={widget}
									data={widgetDataItem}
									onRefresh={() => {
										handleRefresh(widget.id);
									}}
									onRemove={() => {
										handleRemove(widget.id);
									}}
									onTitleChange={(newTitle) => {
										handleTitleChange(widget.id, newTitle);
									}}
									editMode={editMode}
									dashboardFilters={dashboardFilters}
									availableFilters={filterAdapter.getReportFilters(
										widget.reportPath ?? widget.type,
									)}
									customFilters={filterAdapter.getCustomFiltersForWidget(
										widget.reportPath ?? widget.type,
									)}
									dateRangeOptions={filterAdapter.getReportDateRangeOptions(
										widget.reportPath ?? widget.type,
									)}
									dateRangeCalculator={filterAdapter.getReportDateRangeCalculator(
										widget.reportPath ?? widget.type,
									)}
									onWidgetFiltersChange={handleFiltersChangeForWidget}
									renderFilterManager={renderFilterManager}
									renderFullscreenWidget={renderFullscreenWidget}
									hideChrome={hideWidgetChrome}
								>
									{renderWidget(widget, widgetDataItem, {
										dashboardFilters,
										onFiltersChange: (filters) => {
											handleFiltersChangeForWidget(widget.id, filters);
										},
									})}
								</WidgetContainer>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	return (
		<div className="w-full px-6">
			<div
				ref={containerRef}
				onDragOver={handleExternalDragOver}
				onDragLeave={handleExternalDragLeave}
				onDrop={handleExternalDrop}
				className={cn(
					// Fill the viewport rather than centering in a 1400px
					// column so the dashboard matches the rest of the CRM
					// (orders, clients, items lists, etc.) which all
					// flex-1 / w-full to the available width.
					'relative w-full',
					editMode &&
						cn(
							'rounded-lg border-2 border-dashed',
							colors.classes.bgSecondary,
							colors.classes.borderStrong,
						),
					// Subtle outer outline while an external drag is in
					// flight, on top of which the drop-target preview box
					// renders at the exact landing cell. Strong outline
					// across the whole grid would compete with the preview
					// box for the user's attention.
					isExternalDragOver && !editMode && 'rounded-lg',
				)}
				style={
					isExternalDragOver && !editMode
						? {
								boxShadow: `inset 0 0 0 1px ${colors.values.info}`,
							}
						: undefined
				}
			>
				<div
					className="relative"
					style={{
						minHeight: `${minHeight}px`,
						width: '100%',
					}}
				>
					{/* Grid lines for edit mode */}
					{editMode && (
						<div className="absolute inset-0 pointer-events-none">
							{/* Vertical lines */}
							{Array.from({ length: GRID_COLUMNS + 1 }, (_, i) => (
								<div
									key={`v-${i}`}
									className="absolute top-0 bottom-0 w-px"
									style={{
										left:
											i === GRID_COLUMNS
												? actualContainerWidth - 1 // Last line at right edge
												: i * (columnWidth + GRID_GAP) -
													(i === 0 ? 0 : GRID_GAP / 2),
										backgroundColor: draggedWidgetId
											? 'rgba(59, 130, 246, 0.4)'
											: 'rgba(59, 130, 246, 0.15)',
									}}
								/>
							))}
							{/* Horizontal lines */}
							{Array.from(
								{ length: Math.ceil(minHeight / (GRID_ROW_HEIGHT + GRID_GAP)) },
								(_, i) => (
									<div
										key={`h-${i}`}
										className="absolute left-0 right-0 h-px"
										style={{
											top:
												i * (GRID_ROW_HEIGHT + GRID_GAP) -
												(i === 0 ? 0 : GRID_GAP / 2),
											backgroundColor: draggedWidgetId
												? 'rgba(59, 130, 246, 0.4)'
												: 'rgba(59, 130, 246, 0.15)',
										}}
									/>
								),
							)}
						</div>
					)}

					{/* Drag preview indicator — shows for both internal widget
					    moves (draggedWidgetId) and sidebar-to-grid drops
					    (isExternalDragOver). Same green box either way so
					    the two flows look identical to the user. */}
					{previewGridPosition && (draggedWidgetId || isExternalDragOver) && (
						<div
							className="absolute pointer-events-none"
							style={{
								left: previewGridPosition.col * (columnWidth + GRID_GAP),
								top: previewGridPosition.row * (GRID_ROW_HEIGHT + GRID_GAP),
								width:
									previewGridPosition.width * columnWidth +
									(previewGridPosition.width - 1) * GRID_GAP,
								height:
									previewGridPosition.height * GRID_ROW_HEIGHT +
									(previewGridPosition.height - 1) * GRID_GAP,
								border: `3px dashed ${colors.values.success}`,
								borderRadius: '8px',
								backgroundColor: `${colors.values.success}25`,
								zIndex: 45,
							}}
						/>
					)}

					{/* Resize preview indicator */}
					{previewGridPosition &&
						resizingWidgetId &&
						widgetLayouts[resizingWidgetId] && (
							<div
								className="absolute pointer-events-none"
								style={{
									left:
										widgetLayouts[resizingWidgetId].position.col *
										(columnWidth + GRID_GAP),
									top:
										widgetLayouts[resizingWidgetId].position.row *
										(GRID_ROW_HEIGHT + GRID_GAP),
									width:
										previewGridPosition.width * columnWidth +
										(previewGridPosition.width - 1) * GRID_GAP,
									height:
										previewGridPosition.height * GRID_ROW_HEIGHT +
										(previewGridPosition.height - 1) * GRID_GAP,
									border: `2px dashed ${colors.values.success}`,
									borderRadius: '8px',
									backgroundColor: `${colors.values.success}15`,
									zIndex: 5,
								}}
							/>
						)}

					{/* Operation status indicator */}
					{(draggedWidgetId || resizingWidgetId) && (
						<div className="absolute top-4 right-4 pointer-events-none z-50">
							<div
								className={cn(
									'rounded-lg shadow-lg px-3 py-2 flex items-center gap-2',
									colors.classes.bgPrimary,
								)}
							>
								<Icon
									icon={draggedWidgetId ? faArrowsAlt : faExpand}
									className={colors.classes.statusInfo}
									size={14}
								/>
								<Text
									className={cn(
										'text-sm font-medium',
										colors.classes.textPrimary,
									)}
								>
									{draggedWidgetId
										? t('Dragging widget')
										: t('Resizing widget')}
									{affectedWidgetIds.length > 0 &&
										t(` (${affectedWidgetIds.length} affected)`)}
								</Text>
							</div>
						</div>
					)}

					{widgets.map((widget) => {
						const layout = widgetLayouts[widget.id];
						if (!layout) {
							return null;
						}

						return (
							<DraggableWidget
								key={widget.id}
								widget={widget}
								layout={layout}
								editMode={editMode}
								onRemove={handleRemove}
								onSettings={onWidgetSettings}
								onTitleChange={handleTitleChange}
								dashboardFilters={dashboardFilters}
								onFiltersChange={handleFiltersChangeForWidget}
								onDrop={handleWidgetDrop}
								columnWidth={columnWidth}
								GRID_GAP={GRID_GAP}
								GRID_ROW_HEIGHT={GRID_ROW_HEIGHT}
								isAffected={affectedWidgetIds.includes(widget.id)}
								previewGridPosition={previewGridPosition}
								widgetData={widgetData[widget.id]}
								onRefresh={handleRefresh}
								onDragStart={() => {
									setDraggedWidgetId(widget.id);
									// Note: We don't update layouts here to avoid unnecessary re-renders during drag
									// The actual position update happens in onDragEnd
								}}
								onDragEnd={handleDragEnd}
								onDragMove={handleDragMove}
								setPreviewGridPosition={setPreviewGridPosition}
								pixelsToGridPositionDragOnly={pixelsToGridPositionDragOnly}
								isResizing={resizingWidgetId === widget.id}
								onResizeStart={() => {
									setResizingWidgetId(widget.id);
								}}
								onResizeMove={handleResizeMove}
								onResizeEnd={handleResizeEnd}
								gridPositionToPixels={gridPositionToPixels}
								pixelsToGridPosition={pixelsToGridPosition}
								snapToGridWidth={snapToGridWidth}
								snapToGridHeight={snapToGridHeight}
								filterAdapter={filterAdapter}
								renderWidget={renderWidget}
								renderFilterManager={renderFilterManager}
								renderFullscreenWidget={renderFullscreenWidget}
								hideChrome={hideWidgetChrome}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
});

WidgetGridComponent.displayName = 'WidgetGrid';

export const WidgetGrid = WidgetGridComponent;
